import { NotificationService } from 'src/app/shared/services/notification.service';
import { Module } from 'src/app/kuba/follow-ups/models/deviation';
import { BaseServices } from 'src/app/kuba/kuba.services';
import { inject, Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Route, ActivatedRoute } from '@angular/router';

import { HelperService } from './../../../../_services/helper.service';
import { TimetableServices } from '../../services/timetable.service';
import { Timetable } from '../../models/timetable';
import { Subscription, forkJoin } from 'rxjs';
import { Status } from 'src/app/_models';

@Component({
  selector: 'new-timetable',
  templateUrl: 'new-timetable.component.html',
  standalone: false
})
export class CreateTimetableComponent implements OnInit {
  //#region variables

  private readonly notifications = inject(NotificationService);
  isTimetableFormHidden = false;
  timetableAddForm: FormGroup;
  loading = false;
  parentKey: any;
  parentId: any;
  isParentEmployee: boolean;
  // "Repeat for additional days" (#4833) — max extra days a base entry can be duplicated to
  readonly maxAdditionalDays = 7;
  repeatLimitExceeded = false;
  private subscriptions: Subscription[] = [];

  // #endregion

  //#region constructor

  /**
   * constructor
   * @param timetableService {TimetableServices}
   * @param _fb {FormBuilder}
   * @param location {Location}
   * @param route {ActivatedRoute}
   */
  constructor(
    private timetableService: TimetableServices,
    private _fb: FormBuilder,
    private location: Location,
    private route: ActivatedRoute
  ) {
    this.parentKey = route.snapshot.parent.parent.data['parent'];
    this.parentId = route.snapshot.parent.parent.params['id'];
  }

  // #endregion

  //#region page-events

  /**
   * page init
   */
  ngOnInit() {
    document.querySelector('body').classList.remove('opened');
    if (this.parentKey === 'EMPLOYEE') {
      this.isParentEmployee = true;
    } else {
      this.isParentEmployee = false;
    }
    this.timetableService.getParent(this.isParentEmployee);

    this.timetableAddForm = this._fb.group({
      EmployeeId: ['', Validators.required],
      ProjectName: [],
      TypeOfWorkId: ['', Validators.required],
      StartDate: ['', Validators.required],
      EndDate: [''],
      StartTime: ['', Validators.required],
      EndTime: ['', Validators.required],
      BreakTime: [''],
      HoursSpent: [''],
      Status: [''],
      ApprovalUserId: ['', Validators.required],
      TimeTableApproverId: [''],
      Comments: [''],
      AdditionalDays: [[]],
      selectedEmployee: [[]],
      selectedTypeofwork: [[]],
      selectedpprovalperson: [[]]
    });
    this.isTimetableFormHidden = true;
  }

  // #endregion

  //#region Control-events

  /**
   * saving TimeTable
   */
  saveProceed() {
    this.repeatLimitExceeded = false;
    const timetable = this.buildTimetablePayload();

    // "Repeat for additional days" (#4833): duplicate the base entry onto each
    // additional day picked in the calendar. Empty selection = original single save.
    const extraDates = this.getUniqueAdditionalDates(timetable.StartDate);
    if (extraDates.length > this.maxAdditionalDays) {
      this.repeatLimitExceeded = true;
      return;
    }

    this.loading = true;

    if (extraDates.length === 0) {
      this.subscriptions.push(
        this.timetableService
          .addTimetable(timetable, this.parentKey)
          .subscribe(IsTimeTableSaved => {
            if (IsTimeTableSaved) {
              this.notifications.success('UI_NOTIFICATIONS.SAVED');
            }
            this.loading = false;
          })
      );
      this.resetTimeTable();
      return;
    }

    // base day + one single-day record per additional day, same hours/type/approvers
    const payloads: Timetable[] = [timetable];
    extraDates.forEach(dateStr => {
      const clone = <Timetable>{ ...timetable };
      clone.StartDate = dateStr;
      clone.EndDate = dateStr;
      payloads.push(clone);
    });

    this.subscriptions.push(
      forkJoin(
        payloads.map(p => this.timetableService.addTimetable(p, this.parentKey))
      ).subscribe(results => {
        if (results.some(r => r)) {
          this.notifications.success('UI_NOTIFICATIONS.SAVED');
        }
        this.loading = false;
        this.resetTimeTable();
      })
    );
  }

  /**
   * Builds the formatted timetable DTO from the current form values.
   * @returns {Timetable}
   */
  private buildTimetablePayload(): Timetable {
    let timetable = <Timetable>this.timetableAddForm.value;
    // format the date to mm/dd/yyyy and time to HH:MM format
    timetable.Id = 0;
    timetable.StartDate = HelperService.formatDateFilter(
      this.timetableAddForm.value.StartDate
    );
    timetable.EndDate = HelperService.formatDateFilter(
      this.timetableAddForm.value.EndDate
    );
    timetable.StartTime = HelperService.formatTime(
      this.timetableAddForm.value.StartTime
    );
    timetable.EndTime = HelperService.formatTime(
      this.timetableAddForm.value.EndTime
    );
    timetable.HoursSpent = this.timetableAddForm.value.HoursSpent;
    timetable.BusinessId = BaseServices.BusinessId;
    let val: any;
    if (this.timetableAddForm.value.ProjectName) {
      val = this.timetableAddForm.value.ProjectName.split('_');
    }
    if (this.parentKey === 'EMPLOYEE') {
      let featureId: number;
      if (val) {
        if (val[0] === 'PROJECT') {
          featureId = Module.PROJECT;
        } else if (val[0] === 'FOODSAFETY') {
          featureId = Module.FOODSAFETY;
        }
        timetable.Featurekey = val[1] ? +val[1] : this.parentId; // project or zone feature key
        timetable.FeatureId = featureId ? featureId : this.parentKey; // // project or zone feature Id
      }
    } else {
      let featureId: number;
      if (this.parentKey == 'FS') {
        featureId = Module.FOODSAFETY;
      } else if (this.parentKey == 'IC') {
        featureId = Module.INTERNALCONTROL;
      } else if (this.parentKey == 'FDV') {
        featureId = Module.FIREDEVIATION;
      } else if (this.parentKey == 'PROJECT') {
        featureId = Module.PROJECT;
      } else if (this.parentKey == 'FRAMEWORK') {
        featureId = Module.FRAMEWORKAGREEMENT;
      } else if (this.parentKey == 'SA') {
        featureId = Module.SERVICEAGREEMENT;
      } else if (this.parentKey == 'KUNDE_EL') {
        featureId = Module.Kunde_EL;
      }
      timetable.FeatureId = featureId;
      timetable.Featurekey = this.parentId;
    }
    timetable.CreatedBy = BaseServices.UserId;
    timetable.ProjectId = val ? +val[1] : 0;
    timetable.Status = Status.Open;
    return timetable;
  }

  /**
   * Normalizes the "additional days" selection into unique formatted dates,
   * dropping any that equal the base start date (that record is already saved).
   * @param baseStartDate {any} formatted start date of the base record
   * @returns {string[]}
   */
  private getUniqueAdditionalDates(baseStartDate: any): string[] {
    const selected: any[] = this.timetableAddForm.value.AdditionalDays || [];
    const seen = new Set<string>();
    const result: string[] = [];
    selected.forEach(day => {
      if (!day) {
        return;
      }
      const formatted = HelperService.formatDateFilter(day);
      if (!formatted || formatted === baseStartDate || seen.has(formatted)) {
        return;
      }
      seen.add(formatted);
      result.push(formatted);
    });
    return result;
  }

  resetTimeTable() {
    this.timetableAddForm.reset();
  }
  /**
   * to go TimeTable List
   */
  gotoBack() {
    this.location.back();
  }

  // #endregion

  ngOnDestroy() {
    this.subscriptions.forEach((sub, i) => {
      sub.unsubscribe();
    });
  }
  //#region methods
  // #endregion
}
